import { CircularProgress, Divider, TextField } from '@material-ui/core'
import React from 'react'
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete'

export const MAPS_SCRIPT_URL = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&callback=loadGoogleMaps`
const ALLOWED_LOCATION_TYPES = {
  ROOFTOP: true,
  RANGE_INTERPOLATED: true,
}
const CITY_TYPES = {
  locality: true,
  sublocality: true,
  administrative_area_level_3: true,
}

const getComponentValue = (component, isShortName = false) =>
  component[isShortName ? 'short_name' : 'long_name']

const extractAddressParts = newPlaceInfo => {
  const streetAddress = {}
  let partsSet = 0

  for (let i = 0; partsSet < 6 && i < (newPlaceInfo.address_components || []).length; i += 1) {
    const addressComponent = newPlaceInfo.address_components[i]

    for (let j = 0; j < (addressComponent.types || []).length; j += 1) {
      const type = addressComponent.types[j]
      if (type === 'street_number' && !streetAddress.number) {
        streetAddress.number = getComponentValue(addressComponent, true)
        partsSet += 1
      } else if (type === 'route' && !streetAddress.streetName) {
        streetAddress.streetName = getComponentValue(addressComponent)
        partsSet += 1
      } else if (CITY_TYPES[type] && !streetAddress.city) {
        streetAddress.city = getComponentValue(addressComponent)
        partsSet += 1
      } else if (type === 'administrative_area_level_1' && !streetAddress.state) {
        streetAddress.state = getComponentValue(addressComponent, true)
        partsSet += 1
      } else if (type === 'postal_code' && !streetAddress.zip) {
        streetAddress.zip = getComponentValue(addressComponent)
        partsSet += 1
      } else if (type === 'country' && !streetAddress.country) {
        streetAddress.country = getComponentValue(addressComponent)
        partsSet += 1
      }
    }
  }

  return streetAddress
}

const canGetAddressFromNewPlaceInfo = newPlaceInfo => {
  if (!newPlaceInfo || !newPlaceInfo.address_components || !newPlaceInfo.geometry) {
    return false
  }

  const streetAddress = extractAddressParts(newPlaceInfo)
  const street = `${streetAddress.number || ''} ${streetAddress.streetName || ''}`.trim()
  const locationType =
    typeof newPlaceInfo.geometry.location_type === 'string'
      ? newPlaceInfo.geometry.location_type
      : String(newPlaceInfo.geometry.location_type || '')

  return Boolean(
    street &&
      streetAddress.city &&
      streetAddress.state &&
      streetAddress.zip &&
      !newPlaceInfo.partial_match &&
      ALLOWED_LOCATION_TYPES[locationType],
  )
}

const getHighlightedText = (text, highlight) => {
  const highlightParts = highlight.split(' ')
  const parts = text.split(' ')

  return (
    <span>
      {parts &&
        parts.map((part, i) => (
          <span
            key={i}
            style={
              highlightParts &&
              highlightParts.some(
                highlight =>
                  highlight &&
                  part
                    .replace(/[^a-z0-9]/gi, '')
                    .toLowerCase()
                    .indexOf(highlight.toLowerCase().replace(/[^a-z0-9]/gi, '')) > -1,
              )
                ? { fontWeight: 'bold' }
                : {}
            }
          >
            {`${part} `}
          </span>
        ))}
    </span>
  )
}

class LocationSearchBox extends React.Component {
  state = { address: this.props.address || '', updated: false }
  placeValidationCache = {}
  placeValidationRequests = {}
  lastSuggestionValidationKey = ''
  isMountedComponent = false

  componentDidMount() {
    this.isMountedComponent = true
  }

  componentWillUnmount() {
    this.isMountedComponent = false
  }

  getSuggestionPlaceId = suggestion => suggestion?.placeId || suggestion?.place_id

  validatePlaceId = placeId => {
    if (!placeId) {
      return Promise.resolve(false)
    }

    if (Object.prototype.hasOwnProperty.call(this.placeValidationCache, placeId)) {
      return Promise.resolve(this.placeValidationCache[placeId])
    }

    if (this.placeValidationRequests[placeId]) {
      return this.placeValidationRequests[placeId]
    }

    const request = geocodeByPlaceId(placeId)
      .then(results => {
        if (!results || !results.length) {
          return false
        }
        return canGetAddressFromNewPlaceInfo(results[0])
      })
      .catch(() => false)
      .then(isValid => {
        this.placeValidationCache[placeId] = isValid
        delete this.placeValidationRequests[placeId]

        if (this.isMountedComponent) {
          this.forceUpdate()
        }
        return isValid
      })

    this.placeValidationRequests[placeId] = request
    return request
  }

  queueSuggestionValidation = (suggestions, input) => {
    const suggestionIds = suggestions
      .map(suggestion => this.getSuggestionPlaceId(suggestion))
      .filter(Boolean)
    const validationKey = `${input || ''}|${suggestionIds.join('|')}`
    if (validationKey === this.lastSuggestionValidationKey) {
      return
    }
    this.lastSuggestionValidationKey = validationKey

    suggestionIds.forEach(this.validatePlaceId)
  }

  getFilteredSuggestions = suggestions =>
    suggestions.filter(suggestion => {
      const placeId = this.getSuggestionPlaceId(suggestion)
      if (!placeId) {
        return false
      }

      return this.placeValidationCache[placeId] !== false
    })

  handleChange = address => {
    this.setState({ address, updated: true })
  }

  handleSelect = async (address, placeId) => {
    const { completeAccountToken, redirectOverride, search, updateAddress } = this.props
    this.setState({ address, updated: true })

    const isValid = await this.validatePlaceId(placeId)
    if (!isValid) {
      return
    }

    updateAddress(address)
    search(address, !redirectOverride, null, null, completeAccountToken, true, placeId)
  }

  static getDerivedStateFromProps(props, state) {
    if (props.address && props.address !== state.address && !state.updated) {
      return {
        address: props.address || '',
        updated: true,
      }
    }
    return null
  }

  render() {
    const { label, showError, submitting } = this.props
    return (
      <div>
        {label && <label htmlFor="searchbox">{label}</label>}
        <div className="Input--attachedSet">
          <div className="searchWrapper">
            <div className="searchBox">
              <PlacesAutocomplete
                googleCallbackName="loadGoogleMaps"
                highlightFirstSuggestion
                onChange={this.handleChange}
                onError={() => {}}
                onSelect={this.handleSelect}
                searchOptions={{ componentRestrictions: { country: ['us'] } }}
                value={this.state.address}
              >
                {({ getInputProps, getSuggestionItemProps, suggestions }) => {
                  this.queueSuggestionValidation(suggestions, this.state.address)
                  const filteredSuggestions = this.getFilteredSuggestions(suggestions)
                  const className = filteredSuggestions.length
                    ? 'autocomplete-dropdown-container autocomplete-dropdown-container__active'
                    : 'autocomplete-dropdown-container'
                  return (
                    <div className="autocomplete-root">
                      {submitting && (
                        <CircularProgress
                          size={15}
                          style={{
                            bottom: '16px',
                            position: 'absolute',
                            right: '6px',
                            zIndex: 1,
                          }}
                        />
                      )}
                      <TextField
                        fullWidth
                        inputProps={{
                          style: {
                            borderRadius: '0px',
                          },
                        }}
                        label="Address"
                        margin="dense"
                        type="search"
                        variant="outlined"
                        color="secondary"
                        {...getInputProps({
                          autoFocus: true,
                          id: 'searchbox',
                          onKeyDown: e => {
                            if (e.keyCode !== 13) {
                              return
                            }

                            e.preventDefault()

                            const selectedSuggestion =
                              filteredSuggestions.find(suggestion => suggestion.active) ||
                              filteredSuggestions[0]
                            if (selectedSuggestion) {
                              this.handleSelect(
                                selectedSuggestion.description,
                                this.getSuggestionPlaceId(selectedSuggestion),
                              )
                            } else {
                              e.preventDefault()
                            }
                          },
                          placeholder: 'Find your location',
                        })}
                        tabIndex="6"
                      />
                      <div className={className}>
                        {filteredSuggestions.map((suggestion, i) => {
                          const className = suggestion.active
                            ? 'autocomplete-suggestion autocomplete-suggestion__active'
                            : 'autocomplete-suggestion'

                          return (
                            <div key={i}>
                              <div
                                {...getSuggestionItemProps(suggestion, {
                                  className,
                                })}
                              >
                                <span>
                                  {getHighlightedText(
                                    suggestion.description,
                                    getInputProps().value,
                                  )}
                                </span>
                              </div>
                              {i < filteredSuggestions.length - 1 && (
                                <Divider light variant="middle" />
                              )}
                            </div>
                          )
                        })}
                        {filteredSuggestions && filteredSuggestions.length > 0 && (
                          <div className="autocomplete-suggestion google-attribute">
                            <span>
                              <img
                                alt="Powered by Google"
                                src="https://images.foodsby.com/third-party/powered_by_google_on_white.png"
                              />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }}
              </PlacesAutocomplete>
              {showError ? (
                <span className="error">Sorry, that&apos;s not a valid location.</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default LocationSearchBox
